/**
 * 「調べた結果」を表示するパネル。
 *
 * 説明文（用語ハイライト付き）と、そこで実行できる対処のボタンを出す。
 */

import type { Fix } from '../types/scenario';
import type { GlossaryEntry } from '../scenario/dataSource';
import type { IpConfigResult } from '../sim/diagnostics';
import { button, clear, el } from './dom';
import { renderTextWithTerms, type TermSource } from './terms';

export interface InspectHooks {
  onClose: () => void;
  onApplyFix: (fixId: string) => void;
  onTermClick: (entry: GlossaryEntry | null, term: string) => void;
}

export class InspectPanel {
  readonly root: HTMLElement;
  private titleNode: HTMLElement;
  private bodyNode: HTMLElement;
  private fixNode: HTMLElement;
  private networkNode: HTMLElement;

  constructor(
    private readonly termSource: TermSource,
    private readonly hooks: InspectHooks,
  ) {
    this.titleNode = el('div', { class: 'inspect-title' });
    this.bodyNode = el('div', { class: 'inspect-body' });
    this.fixNode = el('div', { class: 'inspect-fixes' });
    this.networkNode = el('section', { class: 'inspect-network', hidden: true, 'aria-label': 'このPCのネットワーク設定' });

    this.root = el('div', { class: 'inspect-layer', hidden: true }, [
      el('div', { class: 'inspect-panel' }, [
        this.titleNode,
        this.bodyNode,
        this.networkNode,
        this.fixNode,
        el('div', { class: 'inspect-foot' }, [
          button('閉じる (E / Esc)', () => this.hooks.onClose(), 'btn btn-small btn-primary'),
        ]),
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  show(title: string, description: string, terms: string[] | undefined, fixes: Fix[], network: IpConfigResult | null = null): void {
    this.titleNode.textContent = title;
    clear(this.bodyNode);
    if (description) {
      this.bodyNode.append(
        renderTextWithTerms(description, terms, this.termSource, this.hooks.onTermClick),
      );
    }
    this.setNetwork(network);

    clear(this.fixNode);
    if (fixes.length > 0) {
      this.fixNode.append(el('div', { class: 'inspect-fixlabel', text: 'できること' }));
      for (const fix of fixes) {
        this.fixNode.append(
          button(fix.label, () => this.hooks.onApplyFix(fix.id), 'btn btn-fix'),
        );
      }
    }
    this.root.hidden = false;
  }

  setNetwork(config: IpConfigResult | null): void {
    clear(this.networkNode);
    this.networkNode.hidden = config === null;
    if (!config) return;
    const fields = el('dl', { class: 'inspect-network-fields' });
    const entries = [
      ['IPアドレス', config.address ?? '未設定'],
      ['サブネットマスク', config.subnetMask],
      ['デフォルトゲートウェイ', config.gateway ?? '未設定'],
    ];
    for (const [label, value] of entries) {
      fields.append(el('div', { class: 'inspect-network-row' }, [
        el('dt', { text: label }),
        el('dd', { text: value }),
      ]));
    }
    this.networkNode.append(el('h4', { class: 'inspect-network-title', text: 'このPCのネットワーク設定' }), fields);
    for (const note of config.notes) {
      this.networkNode.append(el('p', { class: 'inspect-network-note', text: note }));
    }
  }

  close(): void {
    this.root.hidden = true;
  }
}
